if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );

        if (this.dataset.updateUrl !== "false") {
          requestAnimationFrame(() => this.applyNamedVariantFromUrl());
        }
      }

      applyNamedVariantFromUrl() {
        try {
          const params = new URLSearchParams(window.location.search);
          // If a numeric ?variant= is present, Shopify already handled it
          if (params.has("variant")) return;

          const optionNames = JSON.parse(this.dataset.productOptions || "[]");
          if (!optionNames.length) return;

          // Check at least one option key is present in the URL
          const keys = optionNames.map((n) =>
            n.toLowerCase().replace(/\s+/g, "-"),
          );
          const hasAny = keys.some((k) => params.has(k));
          if (!hasAny) return;

          // Read all variants from the JSON blob on the page
          const variantsEl = document.getElementById("ProductVariantsData");
          if (!variantsEl) return;
          const variants = JSON.parse(variantsEl.textContent);

          // Find the best-matching variant
          const match = variants.find((v) => {
            return keys.every((key, i) => {
              const urlVal = params.get(key);
              if (!urlVal) return true; // not specified — don't filter on it
              const variantVal = (v["option" + (i + 1)] || "")
                .toLowerCase()
                .replace(/\s+/g, "-");
              return variantVal === urlVal.toLowerCase();
            });
          });
          if (!match) return;

          // Check if the correct variant is already selected
          const selectedEl = this.querySelector("[data-selected-variant]");
          if (selectedEl) {
            try {
              const current = JSON.parse(
                selectedEl.textContent || selectedEl.innerHTML,
              );
              // Only skip if the already-selected variant is in stock.
              // If it's OOS, we still need to run the OOS fallback pipeline.
              if (current?.id === match.id && match.available !== false) return;
            } catch (e) {}
          }

          // Trigger selection via the variant-selects inputs (goes through full pipeline)
          const variantSelects = this.querySelector("variant-selects");
          if (!variantSelects) return;

          optionNames.forEach((name, i) => {
            const val = match["option" + (i + 1)];
            if (!val) return;
            const radio = variantSelects.querySelector(
              `input[type="radio"][value="${CSS.escape(val)}"]`,
            );
            if (radio && !radio.checked) {
              radio.checked = true;
            }
            const select = variantSelects.querySelector(
              `select[name="options[${CSS.escape(name)}]"]`,
            );
            if (select && select.value !== val) {
              select.value = val;
            }
          });

          // Dispatch change on the last input to trigger the pipeline once
          const lastRadio = variantSelects.querySelector(
            'input[type="radio"]:checked',
          );
          const lastSelect = variantSelects.querySelector("select");
          const trigger = lastRadio || lastSelect;
          if (trigger) {
            trigger.dispatchEvent(new Event("change", { bubbles: true }));
          }
        } catch (e) {}
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Optimistic update: resolve the chosen variant from the JSON already
        // on the page and update Add to Cart (state + price) and the displayed
        // price immediately, instead of leaving them stale until the (slow on
        // mobile) section re-render returns. The fetch callback below still
        // re-syncs everything afterwards.
        const localVariant = this._resolveVariantLocally();
        if (localVariant) {
          this.updateVariantInputs(localVariant.id);
          this._applyVariantOptimistically(localVariant);
        }

        const finalOptionValues = this._adjustSizeForColorChange(
          event.target,
          selectedOptionValues,
        );

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        // Only the below-fold [data-pdp-section] chunks reflow on a COLOR change
        // (a size change leaves them untouched), so only then is warming their
        // images worthwhile. Gate the pre-swap preload on an actual colour-swatch
        // click and skip it for combined-listing product swaps (that path
        // crossfades the whole <main> and the preload wouldn't cover its gallery).
        const isColorChange = !!event.target.closest(
          ".product-form__input--swatch",
        );

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            finalOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          preload: isColorChange && !shouldSwapProduct,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      // Resolve the currently-selected variant from #ProductVariantsData (the
      // full variant list rendered into the page) and the picker's selected
      // inputs — no network needed. Returns null when anything is missing so
      // callers can fall back to the fetch-based flow.
      _resolveVariantLocally() {
        try {
          const dataEl = document.getElementById("ProductVariantsData");
          const picker = this.variantSelectors;
          if (!dataEl || !picker) return null;
          const variants = JSON.parse(dataEl.textContent);

          const values = [];
          picker.querySelectorAll("fieldset").forEach((fieldset) => {
            const checked = fieldset.querySelector("input:checked");
            if (checked) values.push(checked.value);
          });
          picker.querySelectorAll("select").forEach((select) => {
            if (select.value) values.push(select.value);
          });
          if (!values.length) return null;

          return (
            variants.find((variant) =>
              values.every(
                (value, index) => variant["option" + (index + 1)] === value,
              ),
            ) || null
          );
        } catch (e) {
          return null;
        }
      }

      // Pre-formatted "{{ variant.price | money_without_trailing_zeros }}"
      // strings keyed by variant id (see #ProductPricesData in
      // main-product.liquid). Read from the page so the price never has to be
      // re-derived/re-formatted in JS.
      _localPriceFor(variantId) {
        try {
          const el = document.getElementById("ProductPricesData");
          if (!el) return null;
          const map = JSON.parse(el.textContent);
          return map[variantId] ?? map[String(variantId)] ?? null;
        } catch (e) {
          return null;
        }
      }

      // Update Add to Cart (state + label) and the displayed price straight
      // from the page JSON — no network. The button label mirrors what
      // buy-buttons.liquid renders ("<addToCart label> - <price>").
      _applyVariantOptimistically(variant) {
        const price = this._localPriceFor(variant.id);

        if (variant.available) {
          const label = window.variantStrings.addToCart;
          this.productForm?.toggleSubmitButton(
            false,
            price ? `${label} - ${price}` : label,
          );
        } else {
          this.productForm?.toggleSubmitButton(
            true,
            window.variantStrings.soldOut,
          );
        }

        if (price) {
          this.querySelectorAll(".product-main__price").forEach((el) => {
            el.textContent = price;
          });
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, preload = false }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        fetch(requestUrl, { signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            // Warm the images the incoming render will inject BEFORE any DOM
            // is swapped, so they land already-cached instead of reflowing /
            // popping in one by one as each download finishes (the visible
            // "jump" on first-time color switches). Capped so a slow network
            // can never stall the swap indefinitely. Skipped when there's no
            // selected variant (OOS combo → the below-fold isn't swapped, and
            // the "unavailable" UI should show without waiting).
            const hasVariant = !!html
              .querySelector("variant-selects [data-selected-variant]")
              ?.innerHTML?.trim();
            const warm =
              preload && hasVariant
                ? this._preloadIncomingImages(html)
                : Promise.resolve();
            return warm.then(() => {
              // A newer request may have superseded this one while we waited.
              if (signal.aborted) return;
              return callback(html);
            });
          })
          .then(() => {
            // A newer request superseded this one during the preload wait —
            // don't move focus back to the stale option.
            if (signal.aborted) return;
            // set focus to last clicked option value (preventScroll avoids page jumping)
            const savedScrollY = window.scrollY;
            document
              .querySelector(`#${targetId}`)
              ?.focus({ preventScroll: true });
            // Some mobile browsers (iOS Safari) ignore preventScroll and scroll to the focused element.
            // Restore the scroll position on the next frame if it was moved.
            // Skip while the sticky-nav scroll pin is holding the page — its
            // per-frame anchor compensation owns the scroll position and this
            // restore would undo it for a frame (visible jitter).
            requestAnimationFrame(() => {
              if (document.body.classList.contains("variant-changing")) return;
              if (window.scrollY !== savedScrollY) {
                window.scrollTo(0, savedScrollY);
              }
            });
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      // Pre-fetch the images inside the fetched (still-detached) section HTML
      // that are about to be swapped into the live page:
      //   1. every [data-pdp-section] chunk whose markup actually differs from
      //      the live one (the only chunks product-variant-metafields.js will
      //      innerHTML-replace), and
      //   2. swatch images inside the replacement <variant-selects>.
      // Detached Image objects mirror srcset/sizes so the browser resolves and
      // caches the exact same candidate URL the real <img> will request (same
      // technique as pdp-gallery's _warmAllMedia). Resolves when everything is
      // cached or after a hard cap — whichever comes first — and never rejects.
      _preloadIncomingImages(html) {
        const PRELOAD_CAP_MS = 900;
        try {
          const candidates = [];

          html.querySelectorAll("[data-pdp-section]").forEach((newEl) => {
            const name = newEl.dataset.pdpSection;
            const live = document.querySelector(
              `[data-pdp-section="${CSS.escape(name)}"]`,
            );
            if (!live) return;
            if (live.innerHTML.trim() === newEl.innerHTML.trim()) return;
            candidates.push(...newEl.querySelectorAll("img"));
          });

          const picker = html.querySelector("variant-selects");
          if (picker) candidates.push(...picker.querySelectorAll("img"));

          const waits = [];
          // Hold references while fetching: an unreferenced detached Image can
          // be GC'd before its fetch finishes, silently aborting the warm-up.
          const refs = (this._preloadImgRefs = []);
          candidates.forEach((img) => {
            let srcset = img.getAttribute("srcset");
            let sizes = img.getAttribute("sizes");
            // responsive-image.liquid renders a <picture> where the candidates
            // live on the <source> elements and the <img> carries only a bare
            // fallback src. Warm the source the browser would actually pick so
            // we cache the displayed candidate, not the unused fallback.
            if (!srcset) {
              const picture = img.closest("picture");
              const source =
                picture &&
                [...picture.querySelectorAll("source")].find(
                  (s) => !s.media || window.matchMedia(s.media).matches,
                );
              if (source) {
                srcset = source.getAttribute("srcset");
                sizes = source.getAttribute("sizes");
              }
            }
            const src = img.getAttribute("src");
            if (!src && !srcset) return;
            const warm = new Image();
            if (sizes) warm.sizes = sizes;
            if (srcset) warm.srcset = srcset;
            if (src) warm.src = src;
            if (warm.complete) return; // already in cache
            refs.push(warm);
            waits.push(
              new Promise((resolve) => {
                warm.addEventListener("load", resolve, { once: true });
                warm.addEventListener("error", resolve, { once: true });
              }),
            );
          });

          if (!waits.length) return Promise.resolve();
          return Promise.race([
            Promise.all(waits),
            new Promise((resolve) => setTimeout(resolve, PRELOAD_CAP_MS)),
          ]);
        } catch (e) {
          return Promise.resolve();
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return async (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            // This path never publishes variantChange, so the sticky-nav scroll
            // pin would otherwise stay frozen until its hard-cap timeout. Signal
            // it to settle now so an unavailable-combo click doesn't lock the
            // page for the full safety-net duration.
            document.dispatchEvent(new CustomEvent("pdp:variant-unavailable"));
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          // Keep updates on a single request path so OOS color switches remain responsive.
          // Using the selected variant HTML avoids a second blocking fetch on every OOS click.
          const contentHtml = html;

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = contentHtml.getElementById(
              `${id}-${this.sectionId}`,
            );
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, contentHtml);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          const _newBtnEl = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`,
          );
          const _isBtnDisabled = _newBtnEl?.hasAttribute("disabled") ?? true;
          const _newBtnText = _newBtnEl
            ?.querySelector("span")
            ?.textContent?.trim();
          this.productForm?.toggleSubmitButton(
            _isBtnDisabled,
            _isBtnDisabled ? window.variantStrings.soldOut : _newBtnText,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html: contentHtml,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      _buildVariantParams(variant) {
        try {
          const optionNames = JSON.parse(this.dataset.productOptions || "[]");
          if (!optionNames.length || !variant) return null;
          const params = new URLSearchParams();
          optionNames.forEach((name, i) => {
            const key = name.toLowerCase().replace(/\s+/g, "-");
            const val = (variant["option" + (i + 1)] || "")
              .toLowerCase()
              .replace(/\s+/g, "-");
            if (val) params.set(key, val);
          });
          const str = params.toString();
          return str ? str : null;
        } catch (e) {
          return null;
        }
      }

      updateURL(url, variant) {
        const variantId = variant?.id ?? variant;
        const namedParams =
          typeof variant === "object" && variant !== null
            ? this._buildVariantParams(variant)
            : null;
        const queryString = namedParams
          ? `?${namedParams}`
          : variantId
            ? `?variant=${variantId}`
            : "";

        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${queryString}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", `${url}${queryString}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      // When a color swatch is clicked, silently snap the size to the first
      // available size for that color before building the request URL.
      // This prevents the OOS-fallback chain from being triggered on repeat
      // color clicks when a non-XS size is selected.
      _adjustSizeForColorChange(eventTarget, selectedOptionValues) {
        // Only act when the user clicked inside the color swatch fieldset
        if (!eventTarget.closest(".product-form__input--swatch")) {
          return selectedOptionValues;
        }

        const variantsEl = document.getElementById("ProductVariantsData");
        if (!variantsEl) return selectedOptionValues;

        let variants;
        try {
          variants = JSON.parse(variantsEl.textContent);
        } catch (e) {
          return selectedOptionValues;
        }

        const clickedColor = (eventTarget.value || "").trim().toLowerCase();
        const firstAvailable = variants.find(
          (v) => v.option1.trim().toLowerCase() === clickedColor && v.available,
        );

        // Color is fully OOS — leave as-is so sold-out messaging renders
        if (!firstAvailable) return selectedOptionValues;

        const variantSelects = this.querySelector("variant-selects");
        if (!variantSelects) return selectedOptionValues;

        const sizeFieldset = variantSelects.querySelector(
          ".product-form__input--pill",
        );
        if (!sizeFieldset) return selectedOptionValues;

        const targetSizeInput = Array.from(
          sizeFieldset.querySelectorAll("input[type='radio']"),
        ).find(
          (input) =>
            (input.value || "").trim().toLowerCase() ===
            firstAvailable.option2.trim().toLowerCase(),
        );

        if (!targetSizeInput || targetSizeInput.checked) {
          // Already on the right size — nothing to change
          return selectedOptionValues;
        }

        const currentChecked = sizeFieldset.querySelector(
          "input[type='radio']:checked",
        );

        // Resolve BOTH ids before touching the DOM. The old order mutated the
        // pills first and only then discovered a missing id — bailing with the
        // ORIGINAL values while the DOM already showed the new size, so the
        // visible selection and the rendered variant disagreed.
        const oldSizeId = currentChecked?.dataset.optionValueId;
        const newSizeId = targetSizeInput.dataset.optionValueId;
        if (!oldSizeId || !newSizeId) return selectedOptionValues;

        // Silently update the DOM (no event dispatch — avoids recursive change loops)
        currentChecked.checked = false;
        targetSizeInput.checked = true;

        // Update the visible selected-value label inside the size fieldset
        const selectedValueEl = sizeFieldset.querySelector(
          "[data-selected-value]",
        );
        if (selectedValueEl)
          selectedValueEl.textContent = firstAvailable.option2;

        return selectedOptionValues.map((id) =>
          id === oldSizeId ? newSizeId : id,
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}
